import { useEffect, useState } from 'react';
import { createRestaurantOffer, updateRestaurantOffer } from '../services/offerService';

const petalIcon = (
  <img
    src={`${process.env.PUBLIC_URL}/images/petals.png`}
    alt=""
    className="offer-petals-icon"
    width="14"
    height="14"
  />
);

const initialForm = {
  title: '',
  petalsRequired: '0',
  creditAmount: '0',
  currency: 'USD',
  isActive: true,
};

/** Allow digits and at most one decimal point. */
function sanitizeDecimalInput(value) {
  const cleaned = String(value).replace(/[^\d.]/g, '');
  const dotIndex = cleaned.indexOf('.');
  if (dotIndex === -1) return cleaned;
  return cleaned.slice(0, dotIndex + 1) + cleaned.slice(dotIndex + 1).replace(/\./g, '');
}

function formFromOffer(offer) {
  if (!offer) return initialForm;

  return {
    title: offer.title || '',
    petalsRequired: String(offer.petalsRequired ?? offer.petals ?? 0),
    creditAmount: String(offer.creditAmount ?? offer.credit ?? 0),
    currency: offer.currency || 'USD',
    isActive: offer.isActive !== false && offer.status !== 'inactive' && offer.status !== 'expired',
  };
}

export default function AddOfferModal({ open, restaurant, offer = null, onClose, onSaved }) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEditing = Boolean(offer?.id);

  useEffect(() => {
    if (!open) return;
    setForm(formFromOffer(offer));
    setSaving(false);
    setError('');
  }, [open, offer]);

  if (!open || !restaurant) return null;

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function updateDecimalField(field, value) {
    updateField(field, sanitizeDecimalInput(value));
  }

  function blockNonDecimalKeys(e) {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End',
    ];
    if (allowedKeys.includes(e.key)) return;
    if (/^\d$/.test(e.key)) return;
    if (e.key === '.' && !e.currentTarget.value.includes('.')) return;
    e.preventDefault();
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const title = form.title.trim();
    const petalsRequired = Number(form.petalsRequired);
    const creditAmount = Number(form.creditAmount);
    const currency = form.currency.trim().toUpperCase();

    if (!title) {
      setError('Offer title is required.');
      return;
    }
    if (!Number.isFinite(petalsRequired) || petalsRequired < 0) {
      setError('Petals must be a valid non-negative number.');
      return;
    }
    if (!Number.isFinite(creditAmount) || creditAmount < 0) {
      setError('Credit amount must be a valid non-negative number.');
      return;
    }
    if (!currency) {
      setError('Currency is required.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        title,
        petalsRequired,
        creditAmount,
        currency,
        isActive: form.isActive,
      };

      const response = isEditing
        ? await updateRestaurantOffer(restaurant.id, offer.id, payload)
        : await createRestaurantOffer(restaurant.id, {
            ...payload,
            totalQuantity: null,
            validityDays: 30,
          });

      onSaved?.(response.data || response);
      onClose();
    } catch (err) {
      setError(err.message || (isEditing ? 'Failed to update offer.' : 'Failed to save offer.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay offers-nested-overlay" onClick={onClose}>
      <div
        className="add-offer-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-offer-title"
      >
        <div className="add-offer-modal-header">
          <div>
            <h2 id="add-offer-title">{isEditing ? 'Edit Offer' : 'Add Offer'}</h2>
            <p>{restaurant.name}</p>
          </div>
          <button type="button" className="add-restaurant-close" onClick={onClose} aria-label="Close">
            &#x2715;
          </button>
        </div>

        <form className="add-offer-form" onSubmit={handleSubmit}>
          <label className="add-restaurant-field">
            <span>Title</span>
            <input
              type="text"
              placeholder="e.g. Welcome Bloom"
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              maxLength={150}
              required
            />
          </label>

          <div className="add-restaurant-row">
            <label className="add-restaurant-field">
              <span>Petals Used</span>
              <div className="add-offer-input-with-icon">
                {petalIcon}
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.petalsRequired}
                  onChange={(e) => updateDecimalField('petalsRequired', e.target.value)}
                  onKeyDown={blockNonDecimalKeys}
                  required
                />
              </div>
            </label>

            <label className="add-restaurant-field">
              <span>Credit Amount</span>
              <div className="add-offer-input-with-icon">
                <span className="add-offer-currency-prefix">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.creditAmount}
                  onChange={(e) => updateDecimalField('creditAmount', e.target.value)}
                  onKeyDown={blockNonDecimalKeys}
                  required
                />
              </div>
            </label>
          </div>

          <label className="add-restaurant-field">
            <span>Currency</span>
            <select
              className="add-offer-select"
              value={form.currency}
              onChange={(e) => updateField('currency', e.target.value)}
            >
              <option value="USD">USD</option>
              <option value="INR">INR</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
          </label>

          <div className="add-restaurant-field">
            <span>Status</span>
            <div className="add-offer-status-toggle" role="group" aria-label="Offer status">
              <button
                type="button"
                className={`add-offer-status-btn ${form.isActive ? 'is-active' : ''}`}
                onClick={() => updateField('isActive', true)}
              >
                Active
              </button>
              <button
                type="button"
                className={`add-offer-status-btn ${!form.isActive ? 'is-active' : ''}`}
                onClick={() => updateField('isActive', false)}
              >
                Inactive
              </button>
            </div>
          </div>

          {error && <div className="add-restaurant-error">{error}</div>}

          <div className="add-restaurant-footer">
            <button type="submit" className="add-restaurant-save" disabled={saving}>
              {saving ? 'Saving...' : isEditing ? 'Update Offer' : 'Save Offer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
